/** @module Sourcing/DeleteSRBase */
define([
	"dojo/_base/declare",
	"csi/action",
	"csi/action/Controller",
	"csi/locale",
	"csi/xml",
	"csi/xml/Search"
], function(declare, csiAction, Controller, locale, xml, Search) {

	return declare(Controller, { // Not extending DeleteController because Delete Form has ViewRef attached
		bRefNode: true,
		quoteNodeCt: null,
		sampleNodeCt: null,
		delNodeType: null,
		delNodeName: null,

		/** @property  to indicate if the action is to delete a Supplier Request
		 * set to true in DeleteSupplierRequest.js
		*/
		isDeleteSupplierRequest: false,

		/**
		 * @override
		 * Return Title for Supplier Request Delete Dialog
		 */
		getTitle: function() {
			const nodeType = this.getNode().$Type;
			this.delNodeType = locale.type(nodeType === "SupplierFactory" ? "Factory" : nodeType);
			this.delNodeName = this.getNode().$Name;

			return this.getString(csiAction.SUFFIX_PROMPT, [this.delNodeType]);
		},

		/**
		 * @override
		 * This gets the report from SupplierRequest.Java and maps to URL
		 */
		onPreActionDeferreds: function(dfds) {
			const quoteSearch = new Search({Operation: "CountByXML"});
			const srNode = this.isDeleteSupplierRequest ? this.getNode() : this.getParent();
			quoteSearch.setLimitPath(srNode.$URL, "Child:LineItems/Child:SRLineItemQuotes");
			const quoteQuery = quoteSearch.getQuery();
			this.appendSupplierItemQuery(quoteQuery);
			const quoteDfd = quoteSearch.execute().then((response) => this.quoteNodeCt = response.getTotalCount());
			dfds.push(quoteDfd);

			const sampleSearch = new Search({Operation: "CountByXML"});
			sampleSearch.setLimitPath(srNode.$URL, "Child:LineItems/Child:SRLineItemSamples");
			const sampleQuery = sampleSearch.getQuery();
			this.appendSampleQuery(sampleQuery);
			const sampleDfd = sampleSearch.execute().then((response) => this.sampleNodeCt = response.getTotalCount());
			dfds.push(sampleDfd);
		},

		/**
		 * Extension point for configuring supplier item query
		 */
		appendSupplierItemQuery: function(q) {
			const qOr = q.Or();
			qOr.Attr("State", xml.OP_EQUALS, "Revision.State:DRAFT", "Child:CurrentRevision")
				.Attr("State", xml.OP_EQUALS, "Revision.State:ABANDONED", "Child:CurrentRevision");

			q.Attr("AdoptionStatus", xml.OP_NOT_EQUALS, "AdoptionStatus:Adopted")
				.Attr("Offline", xml.OP_EQUALS, "false");
		},

		/**
		 * Return query for supplier items to delete
		 * @return {Query}
		 */
		getSupplierItemQuery: function() {
			const q = this.m_writer.createQuery();
			const types = this.getActualSampleAndQuoteType();
			q.Node(xml.PROP_TYPE, xml.OP_EQUALS, types.quoteType);
			this.appendSupplierItemQuery(q);
			return q;
		},

		/**
		 * Extension point for configuring sample query
		 */
		appendSampleQuery: function(q) {
		},

		/** get the actual sample type based on the request type */
		getActualSampleAndQuoteType() {
			const srNode = this.isDeleteSupplierRequest ? this.getNode() : this.getParent();
			const sampleAndQuoteType = {
				sampleType: "",
				quoteType: "",
			};

			switch (srNode.RequestType) {
				case "DataPackageContainerType:Food":
				case "DataPackageContainerType:FoodSKU":
					sampleAndQuoteType.sampleType = "FoodSample";
					sampleAndQuoteType.quoteType = "FoodSupplierItem";
					break;
				case "DataPackageContainerType:FormulatedMaterial":
					sampleAndQuoteType.sampleType = "FormulatedMaterialSample";
					sampleAndQuoteType.quoteType = "FoodSupplierItem";
					break;
				case "DataPackageContainerType:Style":
					sampleAndQuoteType.sampleType = "Sample";
					sampleAndQuoteType.quoteType = "SupplierItem";
					break;
				case "DataPackageContainerType:Material":
					sampleAndQuoteType.sampleType = "MaterialSample";
					sampleAndQuoteType.quoteType = "SupplierItem";
					break;
				default:
					// shouldn't happen 
					sampleAndQuoteType.sampleType = "ProductSample";
					sampleAndQuoteType.quoteType = "SupplierItem";
			}

			return sampleAndQuoteType;
		},

		/**
		 * Return query for samples to delete
		 * @return {Query}
		 */
		getSampleQuery: function() {
			const q = this.m_writer.createQuery();
			const types = this.getActualSampleAndQuoteType();
			q.Node(xml.PROP_TYPE, xml.OP_EQUALS, types.sampleType);

			this.appendSampleQuery(q);
			return q;
		},

		/**
		 * read values of Quotes & Samples and pass accordingly
		 */
		toWriter: function() {
			// if quotes and / or samples were selected, clear them as well
			const req = this.m_action.m_request;
			const w = this.m_writer;

			if (this.m_formData.getFieldValue("DeleteQuotes")) {
				w.deleteQuery(this.getSupplierItemQuery());
			}

			if (this.m_formData.getFieldValue("DeleteSamples")) {
				w.deleteQuery(this.getSampleQuery());
			}

			if (req) {
				w.callMethod(req.m_module, req.m_operation)
					.addParameter("URL", xml.ATTR_REF, this.m_urlNode)
					.addParameter("ParentURL", xml.ATTR_REF, this.m_urlParent)
			} else {
				const node = this.m_cache.getNode(this.m_urlNode);
				if (node && node.$Type == "SupplierRequest") {
					w.deleteNode(this.m_urlNode, true);
				}

			}
		},

		/**
		 * remove associated DOM nodes
		 */
		postProcessSuccess: function() {
			if (this.refNode) {
				this.widget.deleteRow(this.refNode);
			}

			const generalView = this.widget.getSiblingView("General");
			if (generalView) {
				generalView.postActionRefreshData();
			}
			this.inherited(arguments);
		},

		onCreatePopup: function(view) {
			this.inherited(arguments);
			view.getField("DeleteQuotes").m_label = locale.resource((this.quoteNodeCt == 1 ? "DeleteQuote" : "DeleteQuotes"), [this.quoteNodeCt]);
			view.getField("DeleteSamples").m_label = locale.resource((this.sampleNodeCt == 1 ? "DeleteSample" : "DeleteSamples"), [this.sampleNodeCt]);
		},

		getSuccess: function() {
			if (!this.m_formData.getFieldValue("DeleteQuotes"))
				this.quoteNodeCt = 0; // doesn't matter anymore
			if (!this.m_formData.getFieldValue("DeleteSamples"))
				this.sampleNodeCt = 0; // doesn't matter anymore

			const quoteMsg = this.quoteNodeCt + " " + (this.quoteNodeCt == 1 ? locale.type("SupplierItem") : locale.typePlural("SupplierItem"));
			const sampleMsg = this.sampleNodeCt + " " + (this.sampleNodeCt == 1 ? locale.type("Sample") : locale.typePlural("Sample"));

			if (this.quoteNodeCt > 0 && this.sampleNodeCt > 0)
				return locale.resource("DeletedFor", [quoteMsg + ", " + sampleMsg, this.delNodeType + ": " + this.delNodeName]);
			else if (this.quoteNodeCt > 0)
				return locale.resource("DeletedFor", [quoteMsg, this.delNodeType + ": " + this.delNodeName]);
			else if (this.sampleNodeCt > 0)
				return locale.resource("DeletedFor", [sampleMsg, this.delNodeType + ": " + this.delNodeName]);
			else
				return this.inherited(arguments);
		}

	});
});
